import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Alert, Image, Linking } from 'react-native';
import { MdShare, MdThumbUp, MdThumbUpOffAlt } from 'react-icons/md';
import { ORANGE_ACTION } from '../../constants/colors';
import { getPresetAvatarAsset } from '../../assets/avatarRegistry';

// Helper function to get avatar source
const getAvatarSource = (avatarUrl: string): any => {
  if (!avatarUrl) return null;
  
  // If it's a preset avatar, return null (will be handled by getPresetAvatarAsset)
  if (avatarUrl.startsWith('preset_')) {
    return null;
  }
  
  // If it's a network URL
  if (avatarUrl.startsWith('http')) {
    return { uri: avatarUrl };
  }
  
  return null;
};

export interface ShareButtonProps {
  type: 'card' | 'trip' | 'post' | 'user';
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  creator?: CreatorInfo;
  stats?: any;
  userRelation?: {
    isFollowing?: boolean;
    isLiked?: boolean;
  };
  containerStyle?: any;
  buttonGroupStyle?: any;
  baseUrl?: string;
  onCreatorPress?: () => void;
  onFollowPress?: (id?: string) => void;
  onLikePress?: () => void;
  showNativeShare?: boolean;
  showCopyLink?: boolean;
}

/**
 * 创建人信息数据结构
 */
export interface CreatorInfo {
  id: string;
  display_name: string;
  avatar_url?: string;
  bio?: string;
}

const ShareButton: React.FC<ShareButtonProps> = ({
  type,
  id,
  title,
  description = '',
  imageUrl,
  creator,
  stats,
  userRelation,
  containerStyle,
  buttonGroupStyle,
  baseUrl,
  onCreatorPress,
  onFollowPress,
  onLikePress,
  showNativeShare = true,
  showCopyLink = true,
}) => {
  // Debug log removed
  const [copied, setCopied] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [avatarLoaded, setAvatarLoaded] = useState(false);

  const getShareUrl = () => {
    const origin = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
    const url = String(origin).replace(/\/$/, '');
    if (type === 'card') {
      return `${url}/card/${id}`;
    } else if (type === 'trip') {
      return `${url}/trip/${id}`;
    } else if (type === 'post') {
      return `${url}/travel-post/${id}`;
    }
    return `${url}/${id}`;
  };

  /**
   * 复制链接到剪贴板
   */
  const handleCopyLink = async () => {
    const shareUrl = getShareUrl();
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error('Failed to copy with fallback:', err);
        } finally {
          document.body.removeChild(textarea);
        }
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      Alert.alert('失败', '复制链接失败');
    }
  };

  /**
   * 使用原生分享 API (Web Share API)
   */
  const handleNativeShare = async () => {
    const shareUrl = getShareUrl();

    // 检查浏览器是否支持 Web Share API
    if (!navigator.share) {
      handleShowShareMenu();
      return;
    }

    try {
      const shareData = {
        title: `${title}${type === 'card' ? ' - Cubemeet' : ' - 行程'}`,
        text: description || `Check out "${title}"`,
        url: shareUrl,
      };

      await navigator.share(shareData);
    } catch (err: any) {
      // 用户取消分享不算错误
      if (err.name === 'AbortError') {
        return;
      }
      console.error('Share failed:', err);
    }
  };

  /**
   * 分享到 WhatsApp
   */
  const handleShareWhatsApp = () => {
    const shareUrl = getShareUrl();
    const text = `${title}\n${description}\n${shareUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    
    if (typeof window !== 'undefined') {
      window.open(whatsappUrl, '_blank');
    } else {
      Linking.openURL(whatsappUrl).catch(err => console.error('WhatsApp share failed:', err));
    }
  };

  /**
   * 分享到 Twitter
   */
  const handleShareTwitter = () => {
    const shareUrl = getShareUrl();
    const text = `${title}\n${shareUrl}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    
    if (typeof window !== 'undefined') {
      window.open(twitterUrl, '_blank');
    } else {
      Linking.openURL(twitterUrl).catch(err => console.error('Twitter share failed:', err));
    }
  };

  /**
   * 分享到 Telegram
   */
  const handleShareTelegram = () => {
    const shareUrl = getShareUrl();
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`;
    
    if (typeof window !== 'undefined') {
      window.open(telegramUrl, '_blank');
    } else {
      Linking.openURL(telegramUrl).catch(err => console.error('Telegram share failed:', err));
    }
  };

  /**
   * 显示分享菜单
   */
  const handleShowShareMenu = () => {
    // 尝试使用 Web Share API 如果可用
    if (typeof navigator !== 'undefined' && navigator.share) {
      handleNativeShare();
      return;
    }
    
    // 回退到传统方式
    const options = [
      { text: '📋 复制链接', onPress: handleCopyLink },
      { text: '💬 WhatsApp', onPress: handleShareWhatsApp },
      { text: '🐦 Twitter', onPress: handleShareTwitter },
      { text: '📱 Telegram', onPress: handleShareTelegram },
      { text: '✕ 取消', onPress: () => {}, style: 'cancel' },
    ];
    
    // 尝试 Alert.alert，如果在 Web 环境中可能会失败
    try {
      Alert.alert('分享到', '选择分享方式', options);
    } catch (e) {
      console.error('Alert.alert 不支持:', e);
      // 如果 Alert.alert 失败，直接显示复制链接成功的反馈
      handleCopyLink();
    }
  };

  /**
   * 处理创建人头像点击
   */
  const handleCreatorPress = () => {
    if (onCreatorPress) {
      onCreatorPress();
    }
  };

  /**
   * 处理关注按钮点击
   */
  const handleFollowPress = () => {
    if (onFollowPress) {
      // supply the creator id so consumers know who to follow
      try {
        onFollowPress(creator?.id);
      } catch (e) {
        console.error('onFollowPress handler threw:', e);
      }
    } else {
      Alert.alert('提示', '关注功能开发中...');
    }
  };

  /**
   * 处理赞按钮点击
   */
  const handleLikePress = () => {
    if (onLikePress) {
      onLikePress();
    } else {
      Alert.alert('提示', '赞功能开发中...');
    }
  };

  // 完整模式：显示创建人信息 + 分享功能
  if (creator) {
    return (
      <View style={[styles.container, containerStyle]}>
        {/* 创建人信息区 + 关注按钮 - 一行 */}
        <View style={styles.creatorSection}>
          <TouchableOpacity
            style={styles.creatorAvatar}
            onPress={handleCreatorPress}
            activeOpacity={0.7}
          >
            {/* Always render a visible placeholder immediately to avoid layout flash when image is loading. */}
            <View style={styles.avatarPlaceholder}>
              <Text style={styles.avatarPlaceholderText}>
                {creator.display_name?.charAt(0)?.toUpperCase() || 'U'}
              </Text>
            </View>
            {creator.avatar_url ? (
              <Image
                source={
                  (() => {
                    const avatarSrc = getAvatarSource(creator.avatar_url);
                    const presetSrc = getPresetAvatarAsset(creator.avatar_url);
                    return avatarSrc || presetSrc || { uri: creator.avatar_url };
                  })()
                }
                style={[styles.avatarImage, avatarLoaded ? {} : { opacity: 0 }]}
                onLoad={() => setAvatarLoaded(true)}
                onError={(e) => { setAvatarLoaded(false); console.warn('Avatar load failed:', creator.avatar_url, e); }}
              />
            ) : null}
          </TouchableOpacity>

          <View style={styles.creatorInfo}>
            <TouchableOpacity onPress={handleCreatorPress} activeOpacity={0.7}>
              <Text style={styles.creatorName}>{creator.display_name}</Text>
            </TouchableOpacity>
            {creator.bio && (
              <Text style={styles.creatorBio} numberOfLines={1}>
                {creator.bio}
              </Text>
            )}
          </View>

          {/* 按钮组 - 紧凑图标按钮，不显示文字 */}
          <View style={styles.actionButtonsSection}>
            {/* 赞按钮 (圆形，触控友好) */}
            <TouchableOpacity
              style={[styles.iconButton, styles.iconButtonSmall]}
              onPress={handleLikePress}
              activeOpacity={0.7}
            >
              {userRelation && (userRelation as any).isLiked ? (
                <MdThumbUp size={20} color={ORANGE_ACTION} />
              ) : (
                <MdThumbUpOffAlt size={20} color={ORANGE_ACTION} />
              )}
            </TouchableOpacity>

            {/* 分享按钮 (圆形，触控友好) */}
            <TouchableOpacity
              style={[styles.iconButton, styles.iconButtonSmall]}
              onPress={handleShowShareMenu}
              activeOpacity={0.7}
            >
              <MdShare size={20} color={ORANGE_ACTION} />
            </TouchableOpacity>

            {/* 关注按钮 */}
              <TouchableOpacity
                style={[
                  styles.followButton,
                  userRelation && (userRelation as any).isFollowing ? styles.followButtonFollowing : null,
                ]}
                onPress={handleFollowPress}
                activeOpacity={0.7}
                disabled={userRelation && (userRelation as any).isFollowing}
              >
                <Text style={styles.followButtonText}>{(userRelation && (userRelation as any).isFollowing) ? '已關注' : '关注'}</Text>
              </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }

  // 简化模式：仅显示分享功能（兼容 ShareCardButton）
  return (
    <View style={[styles.simpleContainer, containerStyle]}>
      <View style={[styles.buttonGroup, buttonGroupStyle]}>
        {showCopyLink && (
          <ShareButtonItem
            icon="📋"
            label={copied ? '✓ 已复制' : '复制链接'}
            onPress={handleCopyLink}
            variant={copied ? 'success' : 'default'}
          />
        )}

        {showNativeShare && typeof navigator !== 'undefined' && navigator.share && (
          <ShareButtonItem
            icon="📤"
            label="分享"
            onPress={handleNativeShare}
            variant="default"
          />
        )}

        {!showNativeShare && (
          <ShareButtonItem
            icon="🔗"
            label="更多分享"
            onPress={handleShowShareMenu}
            variant="default"
          />
        )}
      </View>
    </View>
  );
};

/**
 * 单个分享按钮项
 */
interface ShareButtonItemProps {
  icon: string;
  label: string;
  onPress: () => void;
  variant?: 'default' | 'success';
}

const ShareButtonItem: React.FC<ShareButtonItemProps> = ({
  icon,
  label,
  onPress,
  variant = 'default',
}) => {
  return (
    <TouchableOpacity
      style={[
        styles.button,
        variant === 'success' && styles.buttonSuccess,
      ]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text style={styles.icon}>{icon}</Text>
      <Text
        style={[
          styles.label,
          variant === 'success' && styles.labelSuccess,
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  // ===== 完整模式样式 =====
  container: {
    // slightly larger vertical padding for better spacing below share controls
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    marginBottom: 8,
  },

  // 创建人信息区 - 一行布局
  creatorSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    justifyContent: 'space-between',
  },

  creatorAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },

  avatarImage: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
  },

  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: '#0ea5a4',
    justifyContent: 'center',
    alignItems: 'center',
  },

  avatarPlaceholderText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },

  creatorInfo: {
    flex: 1,
    minWidth: 0,
  },

  creatorName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#000',
  },

  creatorBio: {
    fontSize: 11,
    color: '#999',
    lineHeight: 14,
  },

  followButton: {
    // slightly smaller pill for a tighter header layout
    paddingVertical: 6,
    paddingHorizontal: 12,
    minWidth: 72,
    height: 36,
    backgroundColor: ORANGE_ACTION,
    borderRadius: 999,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },

  followButtonFollowing: {
    backgroundColor: ORANGE_ACTION,
    opacity: 0.95,
  },

  followButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },

  // 行动按钮区 - 图标按钮，不显示文字
  actionButtonsSection: {
    flexDirection: 'row',
    // reduce spacing between like and share icons slightly
    gap: 8,
    alignItems: 'center',
    flexShrink: 0,
  },

  iconButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },

  iconButtonSmall: {
    width: 44,
    height: 44,
    borderRadius: 22,
    padding: 6,
    backgroundColor: '#fff',
    borderColor: 'transparent',
  },

  // ===== 简化模式样式 =====
  simpleContainer: {
    gap: 16,
  },

  buttonGroup: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'stretch',
  },

  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f5f5f5',
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#ddd',
  },

  buttonSuccess: {
    backgroundColor: '#e8f5e9',
    borderColor: '#4caf50',
  },

  icon: {
    fontSize: 18,
  },

  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0ea5a4',
  },

  labelSuccess: {
    color: '#4caf50',
  },

  urlContainer: {
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#0ea5a4',
  },

  urlLabel: {
    fontSize: 12,
    color: '#999',
    marginBottom: 4,
  },

  url: {
    fontSize: 12,
    color: '#0ea5a4',
    fontFamily: 'monospace',
    lineHeight: 16,
  },

  // 旧样式（兼容性）
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: 8,
    backgroundColor: '#f9f9f9',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#eee',
  },

  actionButtonDisabled: {
    opacity: 0.5,
  },

  actionButtonIcon: {
    fontSize: 14,
  },

  actionButtonLabel: {
    fontSize: 11,
    fontWeight: '500',
    color: '#0ea5a4',
  },

  actionButtonLabelDisabled: {
    color: '#ccc',
  },
});

export default ShareButton;
